import { Component, Input, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import * as Highcharts from 'highcharts';
import { MLPerformanceDistributionService, MLPerformanceDistributionData } from '../../../services/ml-performance-distribution/ml-performance-distribution.service';

/**
 * Predicted Probability Distribution Widget - PLAN-1212
 *
 * Displays a histogram showing the distribution of predicted probability scores across SKU-Locations
 * with summary cards for Low/Medium/High probability categories.
 *
 * Requirements:
 * - Histogram with 10 bars for score ranges 0.0-0.1, 0.1-0.2, ..., 0.9-1.0
 * - Color coding: Red (0.0-0.5), Amber (0.5-0.7), Green (0.7-1.0)
 * - Three summary cards showing counts and percentages for Low/Medium/High categories
 * - Tooltip on hover with range details
 */

@Component({
  selector: 'ml-performance-distribution',
  templateUrl: './ml-performance-distribution.component.html',
  styleUrls: ['./ml-performance-distribution.component.scss'],
  standalone: false
})
export class MLPerformanceDistributionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: MLPerformanceDistributionData | null = null;
  @Input() dataSet?: string; // Optional input to specify which mock data set to use
  @Input() size: 'large' | 'medium' | 'small' = 'large'; // Widget size for layout control

  private readonly destroy$ = new Subject<void>();

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};

  lowCount = 0;
  lowPercentage = 0;
  mediumCount = 0;
  mediumPercentage = 0;
  highCount = 0;
  highPercentage = 0;
  totalCount = 0;

  constructor(private readonly mlPerformanceService: MLPerformanceDistributionService) {}

  ngOnInit(): void {
    // If no data is provided, load from service
    if (this.data) {
      this.initializeChart();
    } else {
      this.loadDataFromService();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (this.data) {
        this.initializeChart();
      } else if (!changes['data'].firstChange) {
        this.loadDataFromService();
      }
    }

    if (changes['dataSet'] && !this.data) {
      this.loadDataFromService();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads data from the service
   */
  private loadDataFromService(): void {
    this.mlPerformanceService
      .getMLPerformanceDistribution(this.dataSet)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.data = data;
          this.initializeChart();
        },
        error: error => {
          console.error('Error loading ML Performance Distribution data:', error);
        }
      });
  }

  private initializeChart(): void {
    if (!this.data?.rangeCounts || this.data.rangeCounts.length !== 10) {
      return;
    }

    // Calculate totals and percentages for summary cards
    this.totalCount = this.data.rangeCounts.reduce((sum, count) => sum + count, 0);

    // Low: 0.0-0.5 (first 5 bars)
    this.lowCount = this.data.rangeCounts.slice(0, 5).reduce((sum, count) => sum + count, 0);
    this.lowPercentage = this.totalCount > 0 ? (this.lowCount / this.totalCount) * 100 : 0;

    // Medium: 0.5-0.7 (bars 5-7)
    this.mediumCount = this.data.rangeCounts.slice(5, 7).reduce((sum, count) => sum + count, 0);
    this.mediumPercentage = this.totalCount > 0 ? (this.mediumCount / this.totalCount) * 100 : 0;

    // High: 0.7-1.0 (last 3 bars)
    this.highCount = this.data.rangeCounts.slice(7, 10).reduce((sum, count) => sum + count, 0);
    this.highPercentage = this.totalCount > 0 ? (this.highCount / this.totalCount) * 100 : 0;

    const categories = ['0.0-0.1', '0.1-0.2', '0.2-0.3', '0.3-0.4', '0.4-0.5', '0.5-0.6', '0.6-0.7', '0.7-0.8', '0.8-0.9', '0.9-1.0'];

    // Define colors based on performance ranges
    const barColors = [
      '#930011',
      '#930011',
      '#930011',
      '#930011',
      '#930011', // Red (0.0-0.5)
      '#cc9e00',
      '#cc9e00', // Amber (0.5-0.7)
      '#5c7b1e',
      '#5c7b1e',
      '#5c7b1e' // Green (0.7-1.0)
    ];

    const totalCount = this.totalCount;

    this.chartOptions = {
      chart: {
        type: 'column',
        height: 400,
        backgroundColor: '#ffffff',
        plotBorderWidth: 1,
        plotBorderColor: '#e0e5eb'
      },
      title: {
        text: undefined
      },
      xAxis: {
        categories: ['', '', '0.0-0.5', '', '', '', '0.5-0.7', '', '0.7-1.0', ''],
        title: {
          text: 'Predicted Probability Range',
          style: {
            fontSize: '12px',
            color: '#8599AD',
            fontWeight: '400'
          }
        },
        labels: {
          style: {
            fontSize: '11px',
            color: '#8599AD',
            fontWeight: '400'
          }
        },
        tickLength: 0,
        lineColor: '#e0e5eb'
      },
      yAxis: {
        title: {
          text: 'Units',
          rotation: -90,
          align: 'middle',
          style: {
            fontSize: '12px',
            color: '#29333d',
            fontWeight: '600'
          }
        },
        labels: {
          style: {
            color: '#8599AD',
            fontSize: '11px',
            fontWeight: '400'
          },
          formatter: function () {
            const value = this.value as number;
            if (value >= 1000000) {
              const millions = value / 1000000;
              return millions % 1 === 0 ? millions + 'M' : millions.toFixed(1) + 'M';
            } else if (value >= 1000) {
              const thousands = value / 1000;
              return thousands % 1 === 0 ? thousands + 'k' : thousands.toFixed(1) + 'k';
            }
            return value.toString();
          }
        },
        gridLineColor: '#eff2f5',
        gridLineWidth: 1
      },
      legend: {
        enabled: false
      },
      plotOptions: {
        column: {
          pointPadding: 0.05,
          borderWidth: 0,
          groupPadding: 0.1,
          dataLabels: {
            enabled: false
          },
          colorByPoint: true,
          colors: barColors
        }
      },
      tooltip: {
        backgroundColor: '#ffffff',
        borderColor: '#e0e5eb',
        borderRadius: 4,
        shadow: false,
        useHTML: true,
        formatter: function () {
          const range = categories[this.x];
          const count = this.y || 0;
          const percentage = totalCount > 0 ? ((count / totalCount) * 100).toFixed(1) : '0.0';

          return `
            <div style="padding: 8px;">
              <div style="font-weight: 700; margin-bottom: 4px;">Score Range: ${range}</div>
              <div style="margin: 4px 0;">
                Count: <strong>${count.toLocaleString()}</strong>
              </div>
              <div style="margin: 4px 0;">
                Percentage: <strong>${percentage}%</strong>
              </div>
            </div>
          `;
        }
      },
      series: [
        {
          type: 'column',
          name: 'SKU-Locations',
          data: this.data.rangeCounts
        }
      ],
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      }
    };
  }

  formatNumber(value: number): string {
    return value.toLocaleString();
  }
}
